import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { format } from 'date-fns';

/**
 * Hook for managing leaves data (Admin view)
 * @param {string} businessId - Current business ID
 */
export const useLeaves = (businessId) => {
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!businessId) {
      setLeaves([]);
      setEmployees([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const [leavesRes, empRes] = await Promise.all([
        api.get(`/leaves?business_id=${businessId}`),
        api.get(`/employees?business_id=${businessId}`)
      ]);
      setLeaves(leavesRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch leave data');
      console.error('Error fetching leaves:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createLeave = async (leaveData) => {
    const res = await api.post('/leaves', { ...leaveData, business_id: businessId });
    await fetchData();
    return res.data;
  };

  const approveLeave = async (leaveId) => {
    const res = await api.put(`/leaves/${leaveId}/approve`);
    await fetchData();
    return res.data;
  };

  const rejectLeave = async (leaveId) => {
    const res = await api.put(`/leaves/${leaveId}/reject`);
    await fetchData();
    return res.data;
  };

  const exportLeaves = async (startDate, endDate, exportFormat = 'excel', employeeId = null, status = null) => {
    const startStr = startDate instanceof Date ? format(startDate, 'yyyy-MM-dd') : startDate;
    const endStr = endDate instanceof Date ? format(endDate, 'yyyy-MM-dd') : endDate;
    
    let url = `/leaves/export/${exportFormat}?business_id=${businessId}&start_date=${startStr}&end_date=${endStr}`;
    if (employeeId && employeeId !== 'all') url += `&employee_id=${employeeId}`;
    if (status && status !== 'all') url += `&status=${status}`;
    
    const res = await api.get(url, { responseType: 'blob' });
    return res.data;
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  // Stats
  const stats = {
    total: leaves.length,
    pending: leaves.filter(l => ['pending_dept_head', 'pending_hr', 'pending'].includes(l.status)).length,
    approved: leaves.filter(l => l.status === 'approved').length,
    rejected: leaves.filter(l => l.status === 'rejected').length,
  };

  return {
    leaves,
    employees,
    loading,
    error,
    stats,
    fetchData,
    createLeave,
    approveLeave,
    rejectLeave,
    exportLeaves,
    getEmployeeName,
  };
};

/**
 * Hook for leave balances
 */
export const useLeaveBalance = (businessId, employeeId) => {
  const [balance, setBalance] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    if (!businessId || !employeeId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.get(`/leaves/balance/${employeeId}?business_id=${businessId}`);
      setBalance(res.data);
    } catch (err) {
      console.error('Error fetching leave balance:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId, employeeId]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  return { balance, loading, fetchBalance };
};
